import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { DevolutionProjectionDetailModel } from 'src/app/models/preliminaries/devolution-projection-detail';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { DevolutionProjectionCaptureTableModel } from 'src/app/models/tables/preliminaries/devolution-projection-capture-table.model';
import { RentalStockModalModel } from 'src/app/models/tables/rental/rental-stock-modal.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class DevolutionProjectionDetailService {

  private readonly _httpClient = inject(HttpClient);

  createDevolutionProjectionDetail(devolutionProjectionDetailContract: DevolutionProjectionDetailModel): Observable<DevolutionProjectionDetailModel> {
    return this._httpClient.post<DevolutionProjectionDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionProjectionDetail.CreateAsync}`,
      devolutionProjectionDetailContract
    );
  }

  createDevolutionProjectionDetails(devolutionProjectionDetails: Array<DevolutionProjectionDetailModel>): Observable<boolean> {
    return this._httpClient.post<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionProjectionDetail.CreateDetailsAsync}`,
      devolutionProjectionDetails);
  }

  deleteDevolutionProjectionDetail(devolutionProjectionDetailId: number): Observable<DevolutionProjectionDetailModel> {
    const params = new HttpParams().set('devolutionProjectionDetailId', devolutionProjectionDetailId);
    return this._httpClient.delete<DevolutionProjectionDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionProjectionDetail.DeleteAsync}`,
      { params: params }
    );
  }

  deleteSomeDevolutionProjectionDetails(devolutionProjectionDetailIds: Array<number>): Observable<boolean> {
    return this._httpClient.put<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionProjectionDetail.DeleteSomeDetailsAsync}`,
      devolutionProjectionDetailIds
    );
  }

  exportDetailConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams().set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString());
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionProjectionDetail.ExportDetailConsultation}`, filters, {
      params,
      responseType: 'blob'
    });
  }

  getAllDevolutionProjectionDetailsWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionProjectionDetail.GetAllDevolutionProjectionDetailsWithPaginationAsync}`,
      filters, { params: params }
    );
  }

  getArticleAndStoreNames(articleId: number, storeId: number): Observable<{ article: string, store: string }> {
    const params = new HttpParams().set('articleId', articleId).set('storeId', storeId);
    return this._httpClient.get<{ article: string, store: string }>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionProjectionDetail.GetArticleAndStoreNamesAsync}`,
      { params: params }
    );
  }

  getArticlesForDetails(branchId: number, costCenterId: number, page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]):
    Observable<PagedResponseModel<RentalStockModalModel>> {
    const params = new HttpParams().set('branchId', branchId).set('costCenterId', costCenterId).set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<RentalStockModalModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionProjectionDetail.GetArticlesForDetailsAsync}`,
      filters, { params: params }
    );
  }

  getColumnsForArticles(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionProjectionDetail.GetColumnsForArticlesAsync}`);
  }

  getColumnsForDetailCapture(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionProjectionDetail.GetColumnsForDetailCaptureAsync}`);
  }

  getColumnsForDetailConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionProjectionDetail.GetColumnsForDetailConsultationAsync}`);
  }

  getDetailsForCapture(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[], devolutionProjectionId: number):
    Observable<PagedResponseModel<DevolutionProjectionCaptureTableModel>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant).set('devolutionProjectionId', devolutionProjectionId);
    return this._httpClient.post<PagedResponseModel<DevolutionProjectionCaptureTableModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionProjectionDetail.GetDetailsForCaptureAsync}`,
      filters, { params: params }
    );
  }

  getDevolutionProjectionDetailById(devolutionProjectionDetailId: number): Observable<DevolutionProjectionDetailModel> {
    const params = new HttpParams().set('devolutionProjectionDetailId', devolutionProjectionDetailId);
    return this._httpClient.get<DevolutionProjectionDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionProjectionDetail.GetByIdAsync}`,
      { params: params }
    );
  }

  getDevolutionProjectionDetailByKeys(devolutionProjectionId: number, articleId: number, storeId: number): Observable<DevolutionProjectionDetailModel> {
    const params = new HttpParams().set('devolutionProjectionId', devolutionProjectionId).set('articleId', articleId).set('storeId', storeId);
    return this._httpClient.get<DevolutionProjectionDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionProjectionDetail.GetDevolutionProjectionDetailByKeysAsync}`,
      { params: params }
    );
  }

  updateDevolutionProjectionDetail(devolutionProjectionDetailContract: DevolutionProjectionDetailModel): Observable<DevolutionProjectionDetailModel> {
    return this._httpClient.put<DevolutionProjectionDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionProjectionDetail.UpdateAsync}`,
      devolutionProjectionDetailContract
    );
  }
}
